import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useLogin } from '../../components/logIncontext';

function ApiSidebar() {
  const { t, i18n } = useTranslation('home');
  const navigate = useNavigate();
  const location = useLocation();
  const { article: currentArticle } = useParams();
  const [openCategories, setOpenCategories] = useState({});
  const [headings, setHeadings] = useState([]);
  const [activeHeading, setActiveHeading] = useState('');
  const hasInitialized = useRef(false);
  const { userData } = useLogin();

  const categories = t('categories', { returnObjects: true }) || [];
  
  // Filtrar categorías según configuración del usuario
  const apiCategory = categories.find((cat) => {
    if (cat.slug === 'api') {
      const isLocalhost = window.location.hostname === 'localhost';
      const apiEnabled = userData?.config?.apiEnabled;
      return isLocalhost || apiEnabled;
    }
    return false;
  });
  
  const formulasCategory = categories.find((cat) => {
    const formulasSlugs = ['formulas', 'formules', 'formule', 'formulas-zh'];
    if (formulasSlugs.includes(cat.slug)) {
      const isLocalhost = window.location.hostname === 'localhost';
      const formulasEnabled = userData?.config?.formulasEnabled;
      return isLocalhost || formulasEnabled;
    }
    return false;
  });

  useEffect(() => {
    if (!hasInitialized.current && (apiCategory || formulasCategory)) {
      const initialOpen = {};

      // Artículos directos API
      if (apiCategory) {
        // Si estamos en cualquier ruta de API, expandir toda la categoría
        if (location.pathname.includes('/api/')) {
          initialOpen['api-category'] = true;
          
          // Expandir artículos directos si existen
          if (apiCategory.articles && apiCategory.articles.length > 0) {
            initialOpen['api-articles'] = true;
          }
          
          // Expandir todas las subcategorías
          apiCategory.subcategories?.forEach((sub) => {
            initialOpen[sub.slug] = true;
          });
        }
        
        // Lógica específica para artículos (mantener para casos específicos)
        apiCategory.articles?.forEach((article) => {
          if (location.pathname.includes(`/${article.slug}`)) {
            initialOpen['api-category'] = true;
            initialOpen['api-articles'] = true;
          }
        });

        // Subcategorías API
        apiCategory.subcategories?.forEach((sub) => {
          sub.articles?.forEach((article) => {
            if (location.pathname.includes(`/${article.slug}`)) {
              initialOpen['api-category'] = true;
              initialOpen[sub.slug] = true;
            }
          });
        });
      }

      // Artículos directos Formulas
      if (formulasCategory) {
        formulasCategory.articles?.forEach((article) => {
          if (location.pathname.includes(`/${article.slug}`)) {
            initialOpen['formulas-category'] = true;
            initialOpen['formulas-articles'] = true;
          }
        });

        // Subcategorías Formulas
        formulasCategory.subcategories?.forEach((sub) => {
          sub.subcategories?.forEach((nestedSub) => {
            nestedSub.articles?.forEach((article) => {
              if (location.pathname.includes(`/${article.slug}`)) {
                initialOpen['formulas-category'] = true;
                initialOpen[sub.slug] = true;
                initialOpen[`${sub.slug}-${nestedSub.slug}`] = true;
              }
            });
          });
        });
      }

      setOpenCategories(initialOpen);
      hasInitialized.current = true;
    }
  }, [apiCategory, formulasCategory, location.pathname]);

  const toggleCategory = (key) => {
    setOpenCategories((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleArticleClick = (categoryPath, articleSlug) => {
    navigate(`/${i18n.language}/${categoryPath}/${articleSlug}`);
  };

  const updateHeadings = () => {
    const articleContent = document.querySelector('.prose');
    if (!articleContent) return;

    const h2Elements = articleContent.querySelectorAll('h2');
    const headingList = Array.from(h2Elements)
      .filter((h2) => h2.innerText.trim() !== '')
      .map((h2) => {
        const id = h2.id || h2.innerText.replace(/\s+/g, '-').toLowerCase();
        if (!h2.id) h2.id = id;
        return { id, text: h2.innerText };
      });

    setHeadings(headingList);
  };

  useEffect(() => {
    const observer = new MutationObserver(updateHeadings);
    const articleContent = document.querySelector('.prose');
    if (articleContent) {
      observer.observe(articleContent, { childList: true, subtree: true });
    }
    updateHeadings();
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    updateHeadings();
    if (location.hash) {
      setTimeout(() => {
        const element = document.getElementById(location.hash.substring(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          setActiveHeading(location.hash.substring(1));
        }
      }, 100);
    }
  }, [location.pathname, location.hash, currentArticle]);

  useEffect(() => {
    const handleScroll = () => {
      const h2Elements = document.querySelectorAll('h2');
      let currentHeading = '';
      h2Elements.forEach((h2) => {
        const rect = h2.getBoundingClientRect();
        if (rect.top <= 100 && rect.bottom > 0) {
          currentHeading = h2.id;
        }
      });
      setActiveHeading(currentHeading);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Solo mostrar si hay al menos una categoría disponible
  if (!apiCategory && !formulasCategory) return null;

  return (
    <div className="p-4 sm:p-6 rounded-lg shadow-md sticky top-28 bg-gray-50 text-gray-800 dark:bg-grayButton dark:text-gray-200">
      {/* Categoría API */}
      {apiCategory && (
        <div className="mb-6">
          <div
            className="flex items-center justify-between font-semibold cursor-pointer group text-gray-700 hover:text-secondBg dark:text-gray-300 dark:hover:text-secondBg"
            onClick={() => toggleCategory('api-category')}
          >
            <h2 className="font-bold text-lg text-gray-800 dark:text-gray-100">{apiCategory.title}</h2>
            {openCategories['api-category'] ? <FaChevronUp /> : <FaChevronDown />}
          </div>

          <AnimatePresence>
            {openCategories['api-category'] && (
              <motion.div
                className="ml-2 mt-2"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                {/* Artículos directos API */}
                {apiCategory.articles && apiCategory.articles.length > 0 && (
                  <div className="mb-4">
                    <div
                      className="flex items-center justify-between font-semibold cursor-pointer group text-gray-700 hover:text-secondBg dark:text-gray-300 dark:hover:text-secondBg"
                      onClick={() => toggleCategory('api-articles')}
                    >
                      <h3 className="text-base">Articles</h3>
                      {openCategories['api-articles'] ? <FaChevronUp /> : <FaChevronDown />}
                    </div>

                    <AnimatePresence>
                      {openCategories['api-articles'] && (
                        <motion.div
                          className="ml-2 mt-2"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          {apiCategory.articles.map((article) => {
                            const path = `/${i18n.language}/api/${article.slug}`;
                            const isSelected = location.pathname === path;
                            const articleKey = `article-${article.slug}`;
                            return (
                              <div key={article.slug}>
                                <div className="flex items-center justify-between">
                                  <li
                                    className={`flex py-1 items-center cursor-pointer hover:text-secondBg dark:hover:text-secondBg text-sm flex-1 ${isSelected ? 'font-bold text-secondBg' : 'text-gray-700 dark:text-gray-300'
                                      }`}
                                    onClick={() => handleArticleClick('api', article.slug)}
                                  >
                                    {article.title}
                                  </li>
                                  {isSelected && headings.length > 0 && (
                                    <button
                                      className="ml-2 text-gray-700 hover:text-secondBg"
                                      onClick={() => toggleCategory(articleKey)}
                                    >
                                      {openCategories[articleKey] ? (
                                        <FaChevronUp className="w-3 h-3" />
                                      ) : (
                                        <FaChevronDown className="w-3 h-3" />
                                      )}
                                    </button>
                                  )}
                                </div>

                                {/* Contenido del artículo seleccionado */}
                                {isSelected && openCategories[articleKey] && headings.length > 0 && (
                                  <motion.div
                                    className="ml-4 mt-2"
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.3 }}
                                  >
                                    {headings.map((heading) => (
                                      <div
                                        key={heading.id}
                                        className={`text-sm py-1 cursor-pointer hover:text-secondBg dark:hover:text-secondBg ${activeHeading === heading.id
                                            ? 'text-secondBg font-bold'
                                            : 'text-gray-600 dark:text-gray-400'
                                          }`}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          const currentUrl = new URL(window.location);
                                          currentUrl.hash = heading.id;
                                          window.history.pushState({}, '', currentUrl);
                                          document.getElementById(heading.id)?.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                      >
                                        {heading.text}
                                      </div>
                                    ))}
                                  </motion.div>
                                )}
                              </div>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {/* Subcategorías API */}
                {apiCategory.subcategories?.map((sub) => (
                  <div key={sub.slug} className="mb-4">
                    <div
                      className="flex items-center justify-between font-semibold cursor-pointer group text-gray-700 hover:text-secondBg dark:text-gray-300 dark:hover:text-secondBg"
                      onClick={() => toggleCategory(sub.slug)}
                    >
                      <h3 className="text-base">{sub.title}</h3>
                      {openCategories[sub.slug] ? <FaChevronUp /> : <FaChevronDown />}
                    </div>

                    <AnimatePresence>
                      {openCategories[sub.slug] && (
                        <motion.div
                          className="ml-2 mt-2"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          {sub.articles?.map((article) => {
                            const fullPath = `/${i18n.language}/api/${sub.slug}/${article.slug}`;
                            const isSelected = location.pathname === fullPath;
                            const articleKey = `article-${sub.slug}-${article.slug}`;
                            return (
                              <div key={article.slug}>
                                <div className="flex items-center justify-between">
                                  <li
                                    className={`flex py-1 items-center cursor-pointer hover:text-secondBg dark:hover:text-secondBg text-sm flex-1 ${isSelected ? 'font-bold text-secondBg' : 'text-gray-700 dark:text-gray-300'
                                      }`}
                                    onClick={() => handleArticleClick(`api/${sub.slug}`, article.slug)}
                                  >
                                    {article.title}
                                  </li>
                                  {isSelected && headings.length > 0 && (
                                    <button
                                      className="ml-2 text-gray-700 hover:text-secondBg"
                                      onClick={() => toggleCategory(articleKey)}
                                    >
                                      {openCategories[articleKey] ? (
                                        <FaChevronUp className="w-3 h-3" />
                                      ) : (
                                        <FaChevronDown className="w-3 h-3" />
                                      )}
                                    </button>
                                  )}
                                </div>

                                {/* Contenido del artículo seleccionado en subcategoría */}
                                {isSelected && openCategories[articleKey] && headings.length > 0 && (
                                  <motion.div
                                    className="ml-4 mt-2"
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.3 }}
                                  >
                                    {headings.map((heading) => (
                                      <div
                                        key={heading.id}
                                        className={`text-sm py-1 cursor-pointer hover:text-secondBg dark:hover:text-secondBg ${activeHeading === heading.id
                                            ? 'text-secondBg font-bold'
                                            : 'text-gray-600 dark:text-gray-400'
                                          }`}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          const currentUrl = new URL(window.location);
                                          currentUrl.hash = heading.id;
                                          window.history.pushState({}, '', currentUrl);
                                          document.getElementById(heading.id)?.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                      >
                                        {heading.text}
                                      </div>
                                    ))}
                                  </motion.div>
                                )}
                              </div>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Categoría Formulas */}
      {formulasCategory && (
        <div className="mb-6">
          <div
            className="flex items-center justify-between font-semibold cursor-pointer group text-gray-700 hover:text-secondBg dark:text-gray-300 dark:hover:text-secondBg"
            onClick={() => toggleCategory('formulas-category')}
          >
            <h2 className="font-bold text-lg text-gray-800 dark:text-gray-100">{formulasCategory.title}</h2>
            {openCategories['formulas-category'] ? <FaChevronUp /> : <FaChevronDown />}
          </div>

          <AnimatePresence>
            {openCategories['formulas-category'] && (
              <motion.div
                className="ml-2 mt-2"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                {/* Artículos directos Formulas */}
                {formulasCategory.articles && formulasCategory.articles.length > 0 && (
                  <div className="mb-4">
                    <div
                      className="flex items-center justify-between font-semibold cursor-pointer group text-gray-700 hover:text-secondBg dark:text-gray-300 dark:hover:text-secondBg"
                      onClick={() => toggleCategory('formulas-articles')}
                    >
                      <h3 className="text-base">Articles</h3>
                      {openCategories['formulas-articles'] ? <FaChevronUp /> : <FaChevronDown />}
                    </div>

                    <AnimatePresence>
                      {openCategories['formulas-articles'] && (
                        <motion.div
                          className="ml-2 mt-2"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          {formulasCategory.articles.map((article) => {
                            const path = `/${i18n.language}/${formulasCategory.slug}/${article.slug}`;
                            const isSelected = location.pathname === path;
                            const articleKey = `article-formulas-${article.slug}`;
                            return (
                              <div key={article.slug}>
                                <div className="flex items-center justify-between">
                                  <li
                                    className={`flex py-1 items-center cursor-pointer hover:text-secondBg dark:hover:text-secondBg text-sm flex-1 ${isSelected ? 'font-bold text-secondBg' : 'text-gray-700 dark:text-gray-300'
                                      }`}
                                    onClick={() => handleArticleClick(formulasCategory.slug, article.slug)}
                                  >
                                    {article.title}
                                  </li>
                                  {isSelected && headings.length > 0 && (
                                    <button
                                      className="ml-2 text-gray-700 hover:text-secondBg"
                                      onClick={() => toggleCategory(articleKey)}
                                    >
                                      {openCategories[articleKey] ? (
                                        <FaChevronUp className="w-3 h-3" />
                                      ) : (
                                        <FaChevronDown className="w-3 h-3" />
                                      )}
                                    </button>
                                  )}
                                </div>

                                {isSelected && openCategories[articleKey] && headings.length > 0 && (
                                  <motion.div
                                    className="ml-4 mt-2"
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.3 }}
                                  >
                                    {headings.map((heading) => (
                                      <div
                                        key={heading.id}
                                        className={`text-sm py-1 cursor-pointer hover:text-secondBg dark:hover:text-secondBg ${activeHeading === heading.id
                                            ? 'text-secondBg font-bold'
                                            : 'text-gray-600 dark:text-gray-400'
                                          }`}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          const currentUrl = new URL(window.location);
                                          currentUrl.hash = heading.id;
                                          window.history.pushState({}, '', currentUrl);
                                          document.getElementById(heading.id)?.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                      >
                                        {heading.text}
                                      </div>
                                    ))}
                                  </motion.div>
                                )}
                              </div>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {/* Subcategorías Formulas */}
                {formulasCategory.subcategories?.map((sub) => (
                  <div key={sub.slug} className="mb-4">
                    <div
                      className="flex items-center justify-between font-semibold cursor-pointer group text-gray-700 hover:text-secondBg dark:text-gray-300 dark:hover:text-secondBg"
                      onClick={() => toggleCategory(sub.slug)}
                    >
                      <h3 className="text-base">{sub.title}</h3>
                      {openCategories[sub.slug] ? <FaChevronUp /> : <FaChevronDown />}
                    </div>

                    <AnimatePresence>
                      {openCategories[sub.slug] && (
                        <motion.div
                          className="ml-2 mt-2"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          {/* Subcategorías anidadas */}
                          {sub.subcategories?.map((nestedSub) => (
                            <div key={nestedSub.slug} className="mb-3 ml-2">
                              <div
                                className="flex items-center justify-between font-medium cursor-pointer group text-gray-600 hover:text-secondBg dark:text-gray-400 dark:hover:text-secondBg"
                                onClick={() => toggleCategory(`${sub.slug}-${nestedSub.slug}`)}
                              >
                                <h4 className="text-sm">{nestedSub.title}</h4>
                                {openCategories[`${sub.slug}-${nestedSub.slug}`] ? <FaChevronUp className="w-3 h-3" /> : <FaChevronDown className="w-3 h-3" />}
                              </div>
                              
                              <AnimatePresence>
                                {openCategories[`${sub.slug}-${nestedSub.slug}`] && (
                                  <motion.div
                                    className="ml-3 mt-2"
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.3 }}
                                  >
                                    {nestedSub.articles?.map((article) => {
                                      const formulasSlug = formulasCategory.slug;
                                      const fullPath = `/${i18n.language}/${formulasSlug}/${sub.slug}/${nestedSub.slug}/${article.slug}`;
                                      const isSelected = location.pathname === fullPath;
                                      const articleKey = `article-formulas-${sub.slug}-${nestedSub.slug}-${article.slug}`;
                                      return (
                                        <div key={article.slug}>
                                          <div className="flex items-center justify-between">
                                            <li
                                              className={`flex py-1 items-center cursor-pointer hover:text-secondBg dark:hover:text-secondBg text-sm flex-1 ${
                                                isSelected ? 'font-bold text-secondBg' : 'text-gray-700 dark:text-gray-300'
                                              }`}
                                              onClick={() => handleArticleClick(`${formulasSlug}/${sub.slug}/${nestedSub.slug}`, article.slug)}
                                            >
                                              {article.title}
                                            </li>
                                            {isSelected && headings.length > 0 && (
                                              <button
                                                className="ml-2 text-gray-700 hover:text-secondBg"
                                                onClick={() => toggleCategory(articleKey)}
                                              >
                                                {openCategories[articleKey] ? <FaChevronUp className="w-2 h-2" /> : <FaChevronDown className="w-2 h-2" />}
                                              </button>
                                            )}
                                          </div>
                                          
                                          {isSelected && openCategories[articleKey] && headings.length > 0 && (
                                            <motion.div
                                              className="ml-4 mt-2"
                                              initial={{ opacity: 0, height: 0 }}
                                              animate={{ opacity: 1, height: 'auto' }}
                                              exit={{ opacity: 0, height: 0 }}
                                              transition={{ duration: 0.3 }}
                                            >
                                              {headings.map((heading) => (
                                                <div
                                                  key={heading.id}
                                                  className={`text-sm py-1 cursor-pointer hover:text-secondBg dark:hover:text-secondBg ${
                                                    activeHeading === heading.id ? 'text-secondBg font-bold' : 'text-gray-600 dark:text-gray-400'
                                                  }`}
                                                  onClick={(e) => {
                                                    e.preventDefault();
                                                    const currentUrl = new URL(window.location);
                                                    currentUrl.hash = heading.id;
                                                    window.history.pushState({}, '', currentUrl);
                                                    document.getElementById(heading.id)?.scrollIntoView({ behavior: 'smooth' });
                                                  }}
                                                >
                                                  {heading.text}
                                                </div>
                                              ))}
                                            </motion.div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

export default ApiSidebar;
